// src/components/SuspensionBanner.jsx
// Shown at the top of the app when the logged-in user is suspended.
// Listens for any API response with error:"account_suspended".
// Also detects suspension from sessionStorage user object.
// Includes an inline appeal form — submits to POST /api/appeal.
// FIX: getToken now reads localStorage["gt_token"] first (was missing). Ensures banner token check works after hard-refresh.
// Mount this once inside App.jsx, just after <Header />.

import { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";

function getToken() {
  try { return localStorage.getItem("gt_token") || sessionStorage.getItem("gt_token") || localStorage.getItem("token") || null; }
  catch (e) { return null; }
}

function getStoredUser() {
  try { const raw = sessionStorage.getItem("gt_user"); return raw ? JSON.parse(raw) : null; }
  catch (e) { return null; }
}

export default function SuspensionBanner() {
  const [suspended, setSuspended] = useState(false);
  const [showAppeal, setShowAppeal] = useState(false);
  const [appealText, setAppealText] = useState("");
  const [loading, setLoading]       = useState(false);
  const [done, setDone]             = useState(false);
  const [error, setError]           = useState("");

  useEffect(function() {
    // Check on mount — does the stored user row say suspended?
    // (We set is_suspended via the suspension banner event, not via the token)
    function checkSuspension() {
      var u = getStoredUser();
      if (u && u.is_suspended) setSuspended(true);
    }
    checkSuspension();

    // Listen for the custom event fired by any fetch wrapper that gets a
    // 403 account_suspended response
    function handleSuspended() { setSuspended(true); }
    window.addEventListener("account-suspended", handleSuspended);
    return function() { window.removeEventListener("account-suspended", handleSuspended); };
  }, []);

  // Don't show if not suspended or not logged in
  var token = getToken();
  if (!suspended || !token) return null;

  async function submitAppeal(e) {
    e.preventDefault();
    if (!appealText.trim() || appealText.trim().length < 10) {
      setError("Please write at least a sentence explaining your appeal.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      var res  = await apiFetch("/api/appeal", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ appeal_text: appealText.trim() }),
      });
      var data = await res.json();
      if (!res.ok) {
        setError(data.error || "Could not submit appeal. Try again or email hello@gametrait.com.");
        return;
      }
      setDone(true);
    } catch (err) {
      setError("Network error. Please try again or email hello@gametrait.com directly.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-red-950 border-b border-red-800 px-4 py-3 z-40 relative">
      <div className="max-w-4xl mx-auto">

        {done ? (
          <div className="flex items-start gap-3">
            <span className="text-lime-400 text-lg flex-shrink-0">&#10003;</span>
            <div>
              <p className="text-lime-300 text-sm font-semibold">Appeal submitted</p>
              <p className="text-lime-400/70 text-xs mt-0.5">
                We'll review it within 48 hours and email you at the address on your account.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <span className="text-red-400 text-lg flex-shrink-0">&#9888;&#xFE0F;</span>
                <div>
                  <p className="text-white text-sm font-semibold">Your account has been temporarily suspended</p>
                  <p className="text-red-300 text-xs mt-0.5">
                    A report was filed against your account and it's under review. You can still browse but cannot join games or post comments.
                    Check your email for details, or{" "}
                    <a href="mailto:hello@gametrait.com" className="underline hover:text-white transition-colors">
                      email hello@gametrait.com
                    </a>.
                  </p>
                </div>
              </div>
              {!showAppeal && (
                <button
                  type="button"
                  onClick={function() { setShowAppeal(true); }}
                  className="flex-shrink-0 bg-red-700 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                >
                  Appeal
                </button>
              )}
            </div>

            {showAppeal && (
              <form onSubmit={submitAppeal} className="mt-3 border-t border-red-800 pt-3 space-y-2">
                <p className="text-red-200 text-xs font-semibold">Submit an appeal</p>
                <textarea
                  value={appealText}
                  onChange={function(e) { setAppealText(e.target.value); setError(""); }}
                  placeholder="Explain why you think this suspension is a mistake…"
                  rows={3}
                  maxLength={2000}
                  className="w-full bg-red-900/50 border border-red-700 rounded-lg px-3 py-2 text-white text-sm placeholder-red-400/50 focus:outline-none focus:border-red-500 transition-colors resize-none"
                />
                {error && (
                  <p className="text-red-300 text-xs">{error}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading || appealText.trim().length < 10}
                    className="bg-white hover:bg-zinc-100 disabled:opacity-40 text-red-900 text-xs font-bold px-4 py-2 rounded-lg transition-colors"
                  >
                    {loading ? "Submitting…" : "Submit appeal"}
                  </button>
                  <button
                    type="button"
                    onClick={function() { setShowAppeal(false); setError(""); }}
                    className="text-red-400 hover:text-red-200 text-xs px-3 py-2 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
